/* ============================================================
   PRODUCT PAGE — renders the storefront, then decorates it.

   TWO HALVES, AND THE ORDER MATTERS:

     1. RENDER. Everything the customer needs in order to buy comes
        from Shopify through js/shop/product-data.js — price,
        variants, images, availability. No GSAP, no Lenis. If every
        CDN on the page fails, this half still runs.

        ⚠ THIS HALF NOW HAS A NETWORK DEPENDENCY, and it is the one
        thing on the page that does. Shopify is the only source of a
        price, so if Shopify cannot be reached there is no price to
        show — and the page says so, in the buy column, rather than
        falling back to a figure typed into this repo. The rest of
        the page is local content and renders either way.

     2. DECORATE. Scroll beats, smooth scroll, the video. All of it
        behind a `if(gsap)` guard. The homepage does not have this
        guard, and that is why a slow CDN can leave it as a blank
        white screen — see js/main.js:15. A shop page must never be
        one script away from unsellable.

   Rendering happens first because scenes.crossingLines, .marquee
   and .counters all measure the DOM as they initialise. Animating
   markup that has not been written yet measures nothing.
   ============================================================ */

import { getProduct, getCatalog, contentFor, DEFAULT_HANDLE, CLAIMS } from "./product-data.js";
/* The real reviews, exported from Judge.me and generated into a
   module — see the header in reviews-data.js. */
import { PRODUCT_REVIEWS } from "./reviews-data.js";
import { formatMoney, amount, money, saving, savingPercent } from "./money.js";
import { addLine, lineCount, getCart, onCartChange } from "./cart.js";
import { clearCache } from "./shopify.js";
import {
  JUDGEME_SHOP_DOMAIN, JUDGEME_PUBLIC_TOKEN, JUDGEME_CDN, JUDGEME_PLATFORM,
  NEWSLETTER_ENDPOINT, NEWSLETTER_FIELD
} from "./config.js";
import { sized, srcset, W, SIZES } from "./image.js";
import { initHeader } from "./header.js";
import { initVideo, initSound } from "./video.js";
import { ROUTES, productPath } from "./theme-bridge.js";
/* The confirmation pill. It moved out of this file when the home
   page grew an Add to cart of its own — two copies of a
   confirmation are two things to keep in step. */
import { toast } from "./toast.js";
import { watchLayout } from "./st-refresh.js";
import * as scenes from "./scenes.js";
/* The home page sections, for when one has been dropped into the
   product page. templates/product.json is a list of sections now, so
   any home-* section can be added between the blocks of this page —
   and the ones that are shells rather than markup need the same
   initialisers main.js gives them at home. Both calls below are
   no-ops on a product page that has none. See home-blocks.js. */
import { initHomeContent, initHomeScenes } from "./home-blocks.js";

const $  = (s, r = document) => r.querySelector(s);
const el = (tag, cls, html) => {
  const n = document.createElement(tag);
  if(cls)  n.className = cls;
  if(html != null) n.innerHTML = html;
  return n;
};

/* Anything interpolated into a template below goes through this
   first. The data is ours today, but it comes from Shopify tomorrow —
   a product title is merchant-editable text, and merchant-editable
   text ends up in innerHTML on this page. */
const esc = s => String(s ?? "").replace(/[&<>"']/g, c => (
  { "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]
));

const STAR = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.5l1.1-6.5L2.6 9.4l6.5-.9z"/></svg>`;
/* ⚠ PARTIAL STARS, BECAUSE ROUNDING A RATING UP IS OVERSTATING IT.
   This used to fill `i <= Math.round(n)` stars. At 4.9 that is
   harmless — it rounds to five and five is what a 4.9 looks like.
   At 4.50 it paints FIVE FULL STARS beside the numeral "4.5", which
   is the page claiming a perfect score next to the figure that
   disproves it. Math.round also puts 4.5 and 5.0 in the same picture,
   so the one thing the glyphs exist to communicate is lost.

   Each star is now filled by its own fraction: full below the score,
   empty above it, and the one straddling it clipped to the remainder
   (4.50 → four full and a half). Snapped at 5% so 4.98 does not draw
   a 98%-clipped star that reads as a rendering artefact.

   The clip is on a SECOND, absolutely-positioned copy over an empty
   one, rather than a gradient fill on a single svg: `fill` cannot be
   given a hard colour stop, and a mask would lose the flat-colour
   fallback. Sizing comes from the empty star underneath, so the pair
   still measures exactly one glyph wide inside the flex row — the
   nowrap guarantee on .stars depends on that. */
const stars = n => {
  const v = Math.max(0, Math.min(5, Number(n) || 0));
  const empty = STAR.replace("<svg", `<svg class="is-empty"`);
  return `<span class="stars" aria-hidden="true">${
    [1,2,3,4,5].map(i => {
      const fill = Math.max(0, Math.min(1, v - i + 1));
      if(fill >= .95) return STAR;
      if(fill <= .05) return empty;
      return `<span class="star-part" style="--fill:${(fill * 100).toFixed(1)}%">${empty}${STAR}</span>`;
    }).join("")
  }</span>`;
};


/* ============================================================
   1. RENDER
   ============================================================ */

/* WHICH PRODUCT THIS PAGE SELLS — read off the markup, not written
   here.

   There are four product pages now and they all run this one file.
   Each states its own handle on the <body>:

     <body class="pdp-page" data-handle="microfiber-pad">

   Routing is still flat files, by decision — see PRODUCT_PAGES in
   product-data.js. This is what lets four files share one script
   without four copies of it. The handle must match the product's
   handle in Shopify admin exactly; if it does not, the page shows
   the "not available" panel and the console says where to look. */
const HANDLE = document.body.dataset.handle || DEFAULT_HANDLE;

/* Shopify, or an error. Never both, and never a substitute.

   The await is at module top level, so a throw here would abort the
   whole module and leave the page as empty section hosts with no
   explanation. It is caught instead: `product` stays null, the buy
   column paints an error, and every local section below still
   renders — the steps, the specs and the FAQ do not need Shopify
   and should not disappear because it is down. */
let product = null;
let loadError = null;
try{
  product = await getProduct(HANDLE);
  /* Deal picker switched off: the "Mop only" variant is not on sale
     yet, so it leaves the page entirely — not selectable, not the
     default, not a swatch. See hideVariant in caspian-bridge.liquid. */
  const hide = window.CASPIAN?.hideVariant;
  if(product && hide && product.handle === hide.handle){
    const t = String(hide.title).trim().toLowerCase();
    const keep = product.variants.nodes.filter(v => v.title.trim().toLowerCase() !== t);
    if(keep.length && keep.length < product.variants.nodes.length){
      product.variants.nodes = keep;
      product.options = product.options.map(o => ({
        ...o,
        values: o.values.filter(val => keep.some(v =>
          v.selectedOptions.some(s => s.name === o.name && s.value === val)))
      }));
    }
  }
}catch(err){
  loadError = err;
  // The full technical reason goes to the console, never on screen.
  console.error("[Caspian] Shopify load failed:", err.code || "ERROR", err.detail || err.message);
}

/* Local content is available either way — it does not come from
   Shopify. This is why a failed fetch costs the price and the
   gallery, and nothing else. */
const C = product?.content ?? contentFor(HANDLE);

/* The variant currently selected. Single-variant today, but every
   read of price on this page goes through this rather than through
   product.variants.nodes[0], so adding a size or a bundle in Shopify
   needs no change here. */
let current = product
  ? (product.variants.nodes.find(v => v.availableForSale) || product.variants.nodes[0])
  : null;
let qty = 1;

/* Shopify guarantees at least one variant per product, so this is a
   theoretical case — but "theoretical" is how a page ends up
   rendering ₹undefined. No variant means no price means there is
   nothing here to sell, which is the error state, not a special one. */
if(product && !current){
  loadError = { code: "NO_VARIANT",
    userMessage: "This product cannot be bought right now.",
    detail: `Shopify returned product "${HANDLE}" with no variants.` };
  console.error("[Caspian] Shopify load failed:", loadError.code, loadError.detail);
  product = null;
}

/* The figure the coupon actually lands on. Computed once, from the
   same money helpers as everything else — the page never states a
   total that was worked out by hand. Null for a product with no
   coupon, which every caller checks. */
const couponMoney = () => C.coupon && current ? money(
  Math.max(0, amount(current.price) - Number(C.coupon.amount)
              - (dealOn() && dealIsSolo() && !soloVariant() ? Number(DEAL.soloSaving) : 0)),
  current.price.currencyCode
) : null;


/* ---------- gallery ----------
   Slides crossfade rather than cutting. The old version toggled
   display:none, which is instant and free but reads as a flicker
   at a size this large — and it made a swipe impossible, because
   there is nothing to drag when only one slide is in the layout.
   All slides are stacked and composited now; only the active one
   is opaque and hit-testable.

   Three ways to move: the thumbnails, a horizontal swipe, and the
   arrow keys once the gallery has focus. */
function renderGallery(){
  const main   = $(".pg-main");
  const thumbs = $(".pg-thumbs");
  /* Shopify products can have no images attached, and a product with
     none must not render an empty black frame. The featured image is
     the fallback, and if there is not one either the gallery draws
     nothing rather than a broken <img>. */
  const imgs   = product.images.nodes.length
    ? product.images.nodes
    : (product.featuredImage ? [product.featuredImage] : []);
  main.innerHTML = "";
  thumbs.innerHTML = "";

  const pct = savingPercent(current);
  if(pct > 0) main.appendChild(el("span", "pg-badge", `${pct}% off`));

  imgs.forEach((img, i) => {
    const slide = el("div", "pg-slide" + (i === 0 ? " is-on" : ""));
    /* altText and the dimensions are all nullable in Shopify — an
       image uploaded without alt text comes back with altText:null.
       The product title stands in for the alt (an image of the
       product IS the product), and the size attributes are omitted
       rather than written as width="null", which would be invalid
       and would take the aspect ratio with it. */
    const size = img.width && img.height ? ` width="${img.width}" height="${img.height}"` : "";
    /* srcset + sizes, not the raw URL. The store's photography is
       full resolution — the first Smart Mop image is 3024x3780 —
       and this frame is at most 640px wide. Shopify's CDN resizes
       on request; see js/shop/image.js. */
    slide.innerHTML =
      `<img src="${esc(sized(img.url, 960))}"
            srcset="${esc(srcset(img.url, W.gallery))}"
            sizes="${SIZES.gallery}"
            alt="${esc(img.altText || product.title)}"${size}
            draggable="false"
            ${i === 0 ? 'fetchpriority="high"' : 'loading="lazy" decoding="async"'}>`;
    main.appendChild(slide);

    const t = el("button", "pg-thumb");
    t.type = "button";
    t.setAttribute("role", "tab");
    t.setAttribute("aria-selected", String(i === 0));
    t.setAttribute("aria-label", `View image ${i + 1}`);
    t.innerHTML = `<img src="${esc(sized(img.url, 200))}"
                        srcset="${esc(srcset(img.url, W.thumb))}"
                        sizes="${SIZES.thumb}" alt="" loading="lazy" decoding="async">`;
    t.addEventListener("click", () => select(i));
    thumbs.appendChild(t);
  });

  /* THE DEMO CLIP IS NOT IN THE GALLERY.

     There used to be a sixth thumbnail here carrying the how-to-use
     poster. It held no video of its own — tapping it scrolled the
     page down to the steps section, because a second copy of that
     7MB file in the gallery would either be fetched twice or fight
     the first one for decode budget on a phone.

     It is gone because a control that looks like an image and
     behaves like a jump link is a small lie. The clip plays in the
     steps section, where it is captioned, and only there:
     js/modules/video.js starts it when it scrolls into view and
     pauses it when it leaves. Nothing else on the page reaches for
     it. */

  const slides   = [...main.querySelectorAll(".pg-slide")];
  const thumbEls = [...thumbs.querySelectorAll(".pg-thumb")];
  let index = 0;

  /* is-prev is what gives the fade a direction: the slide being
     left behind drifts the way you came from while the new one
     arrives from the way you are going. Without it both would
     simply dissolve in place, which reads as a glitch rather than
     as movement. */
  function select(i, dir = i > index ? 1 : -1){
    if(i === index || i < 0 || i >= slides.length) return;
    slides[index].classList.remove("is-on");
    slides[index].classList.add("is-prev");
    slides[index].style.setProperty("--d", String(-dir));

    slides[i].classList.remove("is-prev");
    slides[i].style.setProperty("--d", String(dir));
    // reflow, so the browser sees the start state before is-on
    // moves it — without this the incoming slide has no transition
    // to run and simply appears.
    void slides[i].offsetWidth;
    slides[i].classList.add("is-on");

    index = i;
    thumbEls.forEach((t, n) => t.setAttribute("aria-selected", String(n === i)));

    /* Keep the active thumbnail in the scroller. With five images
       plus the clip the row scrolls on a phone, and arrowing to an
       image whose thumbnail is off screen leaves nothing selected
       to look at. */
    const t = thumbEls[i];
    if(t) t.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  /* ---------- swipe ----------
     Pointer events, so one path covers touch, pen and a mouse drag.
     The threshold is a tenth of the frame rather than a fixed pixel
     count: the same gesture should mean the same thing on a 360px
     phone and a 700px desktop frame. */
  let x0 = null, y0 = null;
  main.addEventListener("pointerdown", e => {
    if(e.pointerType === "mouse" && e.button !== 0) return;
    x0 = e.clientX; y0 = e.clientY;
  });
  main.addEventListener("pointerup", e => {
    if(x0 == null) return;
    const dx = e.clientX - x0, dy = e.clientY - y0;
    x0 = null;
    // Vertical wins ties — a near-vertical drag on a phone is the
    // page being scrolled, not the gallery being swiped.
    if(Math.abs(dx) < main.clientWidth * .1 || Math.abs(dx) < Math.abs(dy)) return;
    select(index + (dx < 0 ? 1 : -1));
  });
  main.addEventListener("pointercancel", () => { x0 = null; });

  /* ---------- keyboard ---------- */
  main.tabIndex = 0;
  main.setAttribute("aria-roledescription", "carousel");
  main.addEventListener("keydown", e => {
    if(e.key === "ArrowRight"){ e.preventDefault(); select(index + 1); }
    if(e.key === "ArrowLeft") { e.preventDefault(); select(index - 1); }
  });
}


/* ---------- buy column ----------
   EVERY LOCAL BLOCK IN HERE IS OPTIONAL.

   This page is four products now, not one. The Smart Mop has a
   coupon, a promise panel, a review score and a support line in
   js/shop/product-data.js; the pad, the bottle and the detergent
   have none of that. A missing block is not an error and must not
   be a hole — the section simply is not drawn.

   What is NEVER optional is the half that comes from Shopify: the
   title, the description, the price, the variant and the button. */
let GIFT = null;
function renderBuy(){
  const wrap = $("#buy");
  const sav  = saving(current);
  /* The real exported reviews, falling back to the recorded counts
     in product-data.js for any product that has none of its own.
     One source for the stars up here and the section below, so the
     two halves of the page cannot print different numbers. */
  const rv   = aggregate(PRODUCT_REVIEWS[HANDLE] || C.reviews);
  const mode = reviewsMode();

  /* The preview badge is a LIVE NODE in app mode — Judge.me rendered
     it and may still be holding a reference to it. Move it out of
     harm's way before the line below throws the column away, or a
     variant change silently deletes the stars. */
  if(mode === "app") parkBadge();

  /* The free-gift card is server-rendered (sections/main-product.liquid)
     because its value is summed from live product prices. Hold on to
     the node before the column is thrown away, and drop it back into
     its slot below — on every re-render, so a variant change keeps it. */
  GIFT = GIFT || $("#pb-gift");

  wrap.innerHTML = `
    <p class="pb-crumb"><a href="${ROUTES.home}">Caspian</a> /
      <a href="${ROUTES.catalogue}">Products</a> / ${esc(product.title)}</p>

    <h1 class="pb-title">${esc(product.title)}</h1>

    <!-- The rating under the title.

         Judge.me's preview badge when it is configured, so the stars
         beside the price are the live count rather than the frozen
         local one — and so this row can never disagree with the
         section further down the page. The local stars are the
         fallback, on exactly the same condition as the section is.

         The badge is Judge.me's markup and their loader finds it by
         class, so jdgm-widget and jdgm-preview-badge cannot be
         renamed.

         TWO DIFFERENT MOUNTS, because the two modes run their loader
         at different times:

           "app"   an EMPTY SLOT. Judge.me's script has already swept
                   the page by the time this runs, so a div written
                   here is one it has finished looking for — which is
                   exactly why this row used to be a blank gap. The
                   real badge is server-rendered into a hidden host
                   in templates/product.liquid and mountBadge() moves
                   it in below.

           "token" the mount point itself. loadJudgeme() is called by
                   renderReviews(), which runs AFTER this, so their
                   loader finds it on its first sweep. -->
    ${judgemeReady() ? (mode === "app" ? `
    <div class="pb-rating" id="pb-rating"></div>` : `
    <div class="pb-rating">
      <div class="jdgm-widget jdgm-preview-badge" data-id="${esc(shopifyNumericId(product.id))}"></div>
    </div>`) : rv?.count ? `
    <div class="pb-rating">
      ${stars(rv.average)}
      <a href="#reviews">${rv.average} — ${rv.count} reviews</a>
    </div>` : ""}

    <p class="pb-desc">${esc(product.description)}</p>

    <div class="pb-price">
      <span class="pb-now" id="price-now">${formatMoney(current.price)}</span>
      ${current.compareAtPrice ? `<span class="pb-was">${formatMoney(current.compareAtPrice)}</span>` : ""}
      ${sav ? `<span class="pb-save">Save ${formatMoney(sav)}</span>` : ""}
    </div>
    <p class="pb-tax">Inclusive of all taxes.</p>

    ${renderDeal()}

    <!-- The conversion ask. States the RESULTING price, not the
         discount: "₹3,499 after code" is the number being decided
         on, and making the customer subtract it themselves loses
         some of them.

         Only for products that have a coupon. FREEDOM is the Smart
         Mop's code — offering it on a ₹199 pad would be offering
         the pad for nothing. -->
    ${C.coupon && !dealOn() ? `
    <div class="pb-coupon">
      <span class="pb-coupon-l">
        <span class="pb-coupon-head">${esc(C.coupon.headline)} —
          pay <em>${formatMoney(couponMoney())}</em></span>
        <span class="pb-coupon-sub">${esc(C.coupon.terms)}</span>
      </span>
      <button type="button" class="pb-code" id="coupon-code"
              aria-label="Copy coupon code ${esc(C.coupon.code)}">
        <span>${esc(C.coupon.code)}</span><i>COPY</i>
      </button>
    </div>` : ""}

    ${GIFT ? `<div id="pb-gift-slot"></div>` : ""}

    ${dealOn() ? "" : renderOptions()}

    <div class="pb-actions">
      <div class="qty">
        <button type="button" id="qty-down" aria-label="Decrease quantity">&minus;</button>
        <input id="qty" type="number" value="1" min="1" max="10" inputmode="numeric" aria-label="Quantity">
        <button type="button" id="qty-up" aria-label="Increase quantity">+</button>
      </div>
      <button type="button" class="btn btn--buy" id="add"
              ${current.availableForSale ? "" : "disabled"}>
        <i class="btn-dot"></i>
        <span class="swap" data-label="${current.availableForSale ? "Add to cart" : "Sold out"}">
          <span>${current.availableForSale ? "Add to cart" : "Sold out"}</span>
        </span>
      </button>

      <!-- Appears the moment there is a cart to view, and stays for
           as long as there is one — including on a later visit, since
           it is driven by the cart itself rather than by "did you
           just click Add".

           Add to cart stays where it is rather than turning into
           this. On the page for the thing you are buying, the button
           that buys it should not disappear because you bought one;
           the quantity stepper is right beside it and a second one
           is a common thing to want. -->
      <a class="btn btn--line pb-view" id="view-cart" href="${ROUTES.cart}" hidden>
        <i class="btn-dot"></i>
        <span class="swap" data-label="View cart"><span>View cart</span></span>
      </a>
    </div>
    <p class="pb-note" id="note" role="status"></p>

    <!-- The compact Caspian Ka Promise list was here, directly under
         the buttons: a heading and three ticked lines. It is gone
         because the SAME three terms are now a full section of their
         own further down the page (renderPromise), built to the
         supplied artwork — and one page stating its warranty twice,
         in two different designs, reads as a page that lost track of
         itself.

         To put it back, restore the .pb-promise block from git and
         nothing else has to change: both versions read the identical
         C.promise object, so they could never disagree — only
         repeat. The styles are still in assets/product.css. -->

    ${C.shippingNote ? `<p class="pb-ship">${esc(C.shippingNote)}</p>` : ""}
    ${C.support ? `<p class="pb-support">${esc(C.support.line)}.
      <a href="tel:${esc(C.support.phone)}">${esc(C.support.phoneDisplay)}</a></p>` : ""}
  `;

  /* The badge back into the row under the title. No-op in every mode
     but "app", and a no-op there too when the metafield is empty —
     judgemeReady() will have drawn the local stars instead and there
     is no #pb-rating slot to move anything into. */
  if(mode === "app") mountBadge();

  if(GIFT){
    $("#pb-gift-slot")?.replaceWith(GIFT);
    // The free-kit card belongs to the kit deal only (see renderDeal).
    GIFT.hidden = dealOn() && dealIsSolo();
  }

  wireBuy();
  wireDeal();
}

/* ---------- the deal picker ----------
   Two cards in the buy column: the mop WITH the free cleaning kit, or
   the mop alone for less. Config is window.CASPIAN.deal from
   snippets/caspian-bridge.liquid, null when switched off in Theme
   settings → Offer ribbon.

   "Mop only" is a real Shopify variant, found by its title. Picking a
   card simply selects that variant, so price, cart and checkout all
   follow Shopify, and cart.js leaves the free kit off that variant.

   UNTIL THAT VARIANT EXISTS the card still shows its price — worked
   out as the kit deal minus soloSaving — so the layout can be seen,
   but adding it to the cart is refused with a note. It never puts the
   ₹3,999 variant in the cart under a ₹3,099 label. */
const DEAL = (typeof window !== "undefined" && window.CASPIAN?.deal) || null;
let dealSolo = false;     // only used while the "Mop only" variant is missing

const dealOn = () => !!(DEAL && product && product.handle === DEAL.withHandle);
const isSoloVariant = v => !!v && v.title.trim().toLowerCase() === DEAL.soloTitle.trim().toLowerCase();
const soloVariant = () => product.variants.nodes.find(isSoloVariant) || null;
const kitVariant  = () => product.variants.nodes.find(v => !isSoloVariant(v) && v.availableForSale)
                       || product.variants.nodes.find(v => !isSoloVariant(v)) || current;
const dealIsSolo  = () => soloVariant() ? isSoloVariant(current) : dealSolo;
const dealPrice   = v => {
  const off = C.coupon ? Number(C.coupon.amount) : 0;
  return Math.max(0, amount(v.price) - off);
};

function renderDeal(){
  if(!dealOn()) return "";
  const cur   = current.price.currencyCode;
  const kitV  = kitVariant();
  const soloV = soloVariant();
  const kitPay  = dealPrice(kitV);
  const soloPay = soloV ? dealPrice(soloV) : Math.max(0, kitPay - Number(DEAL.soloSaving));
  const mrp   = kitV.compareAtPrice ? amount(kitV.compareAtPrice) : null;
  const solo  = dealIsSolo();
  const worth = DEAL.kitWorth ? formatMoney(money(DEAL.kitWorth, cur)) : "";
  const diff  = formatMoney(money(soloPay < kitPay ? kitPay - soloPay : 0, cur));
  const img   = product.featuredImage?.url ? sized(product.featuredImage.url, 160) : "";
  const was   = mrp ? `<s>${formatMoney(money(mrp, cur))}</s>` : "";

  return `
    <div class="pdeal" role="radiogroup" aria-label="Pick your deal">
      <div class="pdeal-head">
        <p class="pdeal-h">Pick your deal</p>
        <span class="pdeal-tag">Best offer till date</span>
      </div>
      <div class="pdeal-opts">
        <button type="button" class="pdeal-opt${solo ? "" : " is-on"}" data-deal="kit" role="radio" aria-checked="${!solo}">
          <span class="pdeal-badge pdeal-badge--kit">Best value</span>
          <span class="pdeal-top"><span class="pdeal-name">Mop + FREE ${esc(DEAL.kitTitle)}</span><i class="pdeal-radio" aria-hidden="true"></i></span>
          <span class="pdeal-media">
            ${img ? `<img src="${esc(img)}" alt="" width="56" height="74">` : ""}
            <b aria-hidden="true">+</b>
            <img src="${esc(DEAL.kitImage)}" alt="" width="56" height="74">
          </span>
          <span class="pdeal-price"><strong>${formatMoney(money(kitPay, cur))}</strong>${was}</span>
          <span class="pdeal-list">
            <em>Includes</em>
            <span>Smart Mop</span>
            ${dealList(DEAL.kitItems)}
          </span>
          <span class="pdeal-save">${worth ? `Kit worth ${worth}, yours free` : "Cleaning kit free"}</span>
        </button>

        <button type="button" class="pdeal-opt${solo ? " is-on" : ""}" data-deal="solo" role="radio" aria-checked="${solo}">
          <span class="pdeal-badge pdeal-badge--solo">Lowest price</span>
          <span class="pdeal-top"><span class="pdeal-name">Mop only, no kit</span><i class="pdeal-radio" aria-hidden="true"></i></span>
          <span class="pdeal-media">
            ${img ? `<img src="${esc(img)}" alt="" width="56" height="74">` : ""}
            <span class="pdeal-ghost">No<br>kit</span>
          </span>
          <span class="pdeal-price"><strong>${formatMoney(money(soloPay, cur))}</strong>${was}</span>
          <span class="pdeal-list">
            <em>Includes</em>
            <span>Smart Mop</span>
            ${dealList(DEAL.soloItems)}
          </span>
          <span class="pdeal-save">${diff} less${mrp ? `. Save ${formatMoney(money(mrp - soloPay, cur))}` : ""}</span>
        </button>
      </div>
      <p class="pdeal-nudge">${solo
        ? `Add the cleaning kit for just <b>${diff}</b> more.${worth ? ` It's worth <b>${worth}</b>.` : ""}`
        : `Good pick.${worth ? ` The kit is worth <b>${worth}</b> and` : " The kit"} costs you only <b>${diff}</b> more than the mop alone.`}</p>
    </div>`;
}

const dealList = items => (items || []).map(t => t.trim()).filter(Boolean)
  .map(t => `<span>${esc(t)}</span>`).join("");

/* The two figures both pickers show — the cards and the sticky bar. */
function dealFigures(){
  const kitPay  = dealPrice(kitVariant());
  const soloV   = soloVariant();
  const soloPay = soloV ? dealPrice(soloV) : Math.max(0, kitPay - Number(DEAL.soloSaving));
  return { kitPay, soloPay, cur: current.price.currencyCode };
}

/* One way to change the deal, used by the cards and by the bar, so
   the two can never disagree about which one is picked. */
function chooseDeal(wantSolo){
  const soloV = soloVariant();
  if(soloV) current = wantSolo ? soloV : kitVariant();
  else dealSolo = wantSolo;
  renderBuy();
  paintBuybar();
}

function wireDeal(){
  if(!dealOn()) return;
  document.querySelectorAll(".pdeal-opt").forEach(b => b.addEventListener("click", () => {
    const wantSolo = b.dataset.deal === "solo";
    chooseDeal(wantSolo);
    document.querySelector(`.pdeal-opt[data-deal="${wantSolo ? "solo" : "kit"}"]`)?.focus();
  }));
}

/* The sticky bar on a phone gets the same choice as a two-way switch
   above its price, so someone who scrolled past the cards still sees
   there are two deals and can change their mind without scrolling up. */
function paintBuybarDeal(){
  const bar = $("#buybar");
  if(!bar || !dealOn()) return;
  let el = $("#buybar-deal");
  if(!el){
    el = document.createElement("div");
    el.className = "buybar-deal"; el.id = "buybar-deal";
    el.setAttribute("role", "radiogroup");
    el.setAttribute("aria-label", "Pick your deal");
    bar.prepend(el);
    bar.classList.add("has-deal");
    el.addEventListener("click", e => {
      const b = e.target.closest("[data-deal]");
      if(b) chooseDeal(b.dataset.deal === "solo");
    });
  }
  const f = dealFigures(), solo = dealIsSolo();
  el.innerHTML = `
    <button type="button" data-deal="kit" role="radio" aria-checked="${!solo}"${solo ? "" : ` class="is-on"`}>
      With free kit <b>${formatMoney(money(f.kitPay, f.cur))}</b></button>
    <button type="button" data-deal="solo" role="radio" aria-checked="${solo}"${solo ? ` class="is-on"` : ""}>
      Mop only <b>${formatMoney(money(f.soloPay, f.cur))}</b></button>`;
}

/* Built from product.options, not hardcoded. A product with one
   option called "Title" and one value called "Default Title" is
   Shopify's way of saying "no options" — showing a selector with a
   single choice on it is noise, so that case renders nothing. */
function renderOptions(){
  // One value is not a choice — covers "Default Title" and a "Pack"
  // left with one value once the draft "Mop only" variant is hidden.
  const real = product.options.filter(o => o.values.length > 1);
  if(!real.length) return "";

  return real.map(opt => `
    <div class="pb-opt" data-option="${esc(opt.name)}">
      <span class="pb-opt-name">${esc(opt.name)}</span>
      <div class="pb-swatches">
        ${opt.values.map(v => {
          const variant = variantFor(opt.name, v);
          return `<button type="button" class="pb-swatch"
                    data-option="${esc(opt.name)}" data-value="${esc(v)}"
                    aria-pressed="${current.selectedOptions.some(s => s.name === opt.name && s.value === v)}"
                    ${variant && variant.availableForSale ? "" : "disabled"}>${esc(v)}</button>`;
        }).join("")}
      </div>
    </div>`).join("");
}

function variantFor(name, value){
  return product.variants.nodes.find(v =>
    v.selectedOptions.some(s => s.name === name && s.value === value));
}


/* ---------- the rest of the page ---------- */

/* Three cards, each a clip with its caption under it. A step with no
   clip still renders — the card just loses its picture rather than
   the section losing the step. */
function renderSteps(){
  const wrap = $("#steps");
  if(!wrap) return;
  /* Only the Smart Mop has steps. On an accessory page the whole
     section goes rather than standing empty with a heading over
     nothing. */
  if(!C.steps?.length){ $(".psteps")?.remove(); return; }
  wrap.innerHTML = C.steps.map(s => `
    <article class="pstep">
      ${s.video ? `
      <div class="hcard">
        <video data-src="${esc(s.video)}" poster="${esc(s.poster)}"
               muted loop playsinline webkit-playsinline preload="none"
               disablepictureinpicture disableremoteplayback></video>
        <div class="hcard-in"><b>Step ${esc(s.n)}</b></div>
      </div>` : ""}
      <div class="pstep-txt">
        <span class="pstep-n">${esc(s.n)}</span>
        <h3>${esc(s.title)}</h3>
        <p>${esc(s.body)}</p>
      </div>
    </article>`).join("");
}

/* Built from CLAIMS so the headline figures cannot drift from the
   spec table. A value that is not a plain number — "4-5" — carries no
   data-count and scenes.counters skips it, so the markup is written
   here in the exact shape the counter would have built.

   ⚠ CLAIMS ARE THE SMART MOP'S. Coverage, tank, force and battery
   describe the mop and nothing else in the catalogue, so this runs
   only where the markup asks for it — the accessory pages carry no
   #stats element and it returns immediately. Do not "helpfully"
   add one to them. */
function renderStats(){
  const wrap = $("#stats");
  if(!wrap) return;
  /* COVERAGE IS BACK, and it leads the row again — restored on
     request after a pass that had taken "2000 sq.ft." off this page
     entirely. It reads from CLAIMS.coverage, same as the other three,
     so it agrees with the spec table and the listing chip by
     construction rather than by anyone remembering to update three
     places. The homepage stats row states the same figure.

     The claim banner that also carried it is NOT coming back — that
     was removed as its own decision and this is not it. Four figures
     here, and the grid is written for four. */
  const rows = [
    [CLAIMS.coverage, "Coverage"],
    [CLAIMS.tank,     "Water tank capacity"],
    [CLAIMS.force,    "Downward force"],
    [CLAIMS.battery,  "Battery runtime"]
  ];
  wrap.innerHTML = `<div class="ibox-in">${rows.map(([c, label]) => {
    const numeric = /^\d+(\.\d+)?$/.test(c.value);
    return `<div class="stat">
      <strong ${numeric ? `data-count="${c.value}" data-suffix="${esc(c.unit)}" style="--nw:${c.value.length}ch"` : ""}><i class="n">${esc(c.value)}</i><u>${esc(c.unit)}</u></strong>
      <span>${esc(label)}</span>
    </div>`;
  }).join("")}</div>`;
}

/* ---------- the claim banner ----------
   The card under the header: the smartmop lockup, then the claim in
   three lines with the time as the punchline.

   It renders only where there is a `claim` block to render — the
   accessory pages have none, and the section removes itself rather
   than standing empty.

   The lockup is an <img> from the repo, not from Shopify: it is the
   brand mark, not a product photograph, and it must be on screen
   whether or not the store answered. */
function renderClaim(){
  const wrap = $("#claim");
  if(!wrap) return;
  if(!C.claim){ $(".pclaim")?.remove(); return; }

  /* Every word is a mask with the word inside it — the <i> is the
     thing that moves and .pclaim-w is the window it moves through.
     One element cannot be both: overflow:hidden clips its children,
     never itself, so a transform on the masking element just slides
     the whole word down the page. See scenes.claimIn. */
  const c = C.claim;
  const w = (text, cls = "") =>
    `<span class="pclaim-w${cls ? " " + cls : ""}"><i>${esc(text)}</i></span>`;

  /* TWO LINES, NOT THREE, and the first one WRAPS ON ITS OWN.

     It used to be three fixed lines — "Now, Mop 2000 sqft." then "in"
     then the punchline — which stranded "in" alone in the middle of
     the card at every width. The supplied artwork has the lead
     running as one sentence that breaks wherever the card runs out
     ("Now, Mop 2000 / sqft. in"), so the break is the card's to
     decide, not this function's.

     ONE MASK PER WORD, still: the words have to rise separately, and
     a mask around a whole line would lift it as a slab. So the lead
     is split on whitespace here rather than being three fixed spans.
     `figure` keeps .pclaim-fig on its own words so the hook survives
     the split — it is the same sky as the line today, and it is what
     a future accent colour would attach to. */
  const words = [
    ...c.lead.trim().split(/\s+/).map(t => w(t)),
    ...c.figure.trim().split(/\s+/).map(t => w(t, "pclaim-fig")),
    ...c.joiner.trim().split(/\s+/).map(t => w(t))
  ].join("");

  /* The smartmop lockup that sat over the card was removed on request;
     the claim card is the whole banner now. */
  wrap.innerHTML = `
    <div class="pclaim-card">
      <p class="pclaim-line">${words}</p>
      <p class="pclaim-line"><span class="pclaim-punch">
        <span class="pclaim-speed" aria-hidden="true"><i></i><i></i><i></i></span>
        ${w(c.time, "pclaim-time")}
        <span class="pclaim-brush" aria-hidden="true"><svg viewBox="0 0 300 30" preserveAspectRatio="none"><path pathLength="1" d="M8 19 C 60 11, 118 23, 176 15 S 262 11, 292 17"/></svg></span>
      </span></p>
    </div>`;
}

function renderSpecs(){
  const dl = $("#specs");
  if(dl && C.specs?.length){
    /* NO SKU ROW. This used to append the live variant's SKU to the
       end of the table. Removed on request: it is a warehouse code —
       what an order is picked and packed against — and a customer
       deciding whether to buy a mop has no use for it.

       It is not lost. renderSchema() below still states
       `current.sku` in the JSON-LD, which is where a code like that
       genuinely belongs: machine-readable, for Google Shopping and
       anything else reading the structured data, and never on
       screen. Shopify remains the only place it is authored. */
    dl.innerHTML = C.specs.map(s =>
      `<div class="spec-row"><dt>${esc(s.label)}</dt><dd>${esc(s.value)}</dd></div>`).join("");
  }

  const ul = $("#inbox");
  if(ul && C.inTheBox?.length) ul.innerHTML = C.inTheBox.map(i => `<li>${esc(i)}</li>`).join("");

  // Neither list, no section. A "Specifications" heading with an
  // empty definition list under it is worse than no heading.
  if(!C.specs?.length && !C.inTheBox?.length) $(".specs")?.remove();
}

/* ============================================================
   REVIEWS — Judge.me, with the local counts behind it.

   The reviews on this site are now the SAME reviews as on the
   Shopify storefront, served by Judge.me from the one account. No
   copying, no second place to moderate, and nothing in this repo
   claiming a rating it cannot substantiate.

   ⚠ WHY THE WIDGET AND NOT THE API. Judge.me's REST API needs the
   PRIVATE api_token, which reads and writes every review on the
   account and must never ship in client-side JavaScript. The widget
   uses the PUBLIC token, which is designed for exactly this. So the
   markup below is Judge.me's own — we style it, we do not draw it.

   TWO THINGS HAVE TO BE TRUE for it to load, and both are in the
   Judge.me admin rather than in this repo:

     1. JUDGEME_PUBLIC_TOKEN is set in js/shop/config.js
     2. Settings → Advanced → platform-independent widgets is ON.
        Judge.me refuses to answer for a domain that is not the
        Shopify storefront until this is enabled, and the failure is
        silent — an empty widget, no console error of its own.

   If either is missing, renderReviews() falls back to the local
   distribution and logs one line saying which. The section never
   goes blank.
   ============================================================ */

/* Judge.me identifies a product by its NUMERIC Shopify id. The
   Storefront API gives a global id — "gid://shopify/Product/12345"
   — so the number has to come off the end of it.

   Returns null rather than a guess if the shape is not what is
   expected: mounting the widget with a bad id shows another
   product's reviews, which is worse in every way than showing the
   local fallback. */
function shopifyNumericId(gid){
  const s = String(gid || "");
  const m = /\/Product\/(\d+)/.exec(s);
  if(m) return m[1];
  /* A bare numeric id, which is the shape Liquid's product.id has.
     snippets/product-json.liquid wraps it in a gid:// so the rest of
     this file keeps the Storefront API's shape, but if that snippet
     is ever simplified this keeps Judge.me keyed correctly instead of
     silently falling back to the local counts. */
  return /^\d+$/.test(s) ? s : null;
}

/* WHO IS DRAWING THE REVIEWS — "app", "token" or "local".

   Theme settings → Reviews, stamped onto the section by
   templates/product.liquid. Read from the DOM rather than passed
   around because the section is server-rendered and therefore
   already in the page before this module runs, and because one
   reader means the badge, the section and the JSON-LD cannot end up
   disagreeing about which mode the page is in.

   Defaults to "token", which is what this file did before the
   setting existed. */
function reviewsMode(){
  return $("#reviews")?.dataset.reviews || "token";
}

/* THE PREVIEW BADGE — the stars beside the price.

   In "app" mode it is SERVER-RENDERED, into a hidden host in
   templates/product.liquid, and moved into the buy column by
   mountBadge() below. That is the whole reason the host exists:
   renderBuy() runs after Judge.me's script has already swept the
   page, so a badge div this file creates is a div their loader has
   already finished looking for. It stayed empty. Rendering it in
   Liquid means it is in the HTML before their script runs, and
   moving a live node keeps whatever they put in it.

   In "token" mode this file loads the widget itself and their
   loader runs after the badge is written, so there is nothing to
   move — renderBuy() writes the mount point directly. */
const badgeNode  = () => document.querySelector(".jdgm-preview-badge");
const badgeHost  = () => $("#jdgm-badge-host");

/* Present is not the same as filled. The host always renders the
   div; the metafield inside it is empty until Judge.me has synced
   reviews for this product. An empty badge is a gap where the stars
   should be, so it counts as not ready and the local stars draw. */
function badgeReady(){
  const b = badgeNode();
  return Boolean(b) && (b.children.length > 0 || b.textContent.trim() !== "");
}

/* Back to the host, before renderBuy() replaces the buy column's
   innerHTML and destroys it. renderBuy() runs again on every variant
   change, so this is not a one-off. */
function parkBadge(){
  const host = badgeHost(), b = badgeNode();
  if(host && b && b.parentNode !== host) host.appendChild(b);
}

/* And into the row under the title, after. */
function mountBadge(){
  const slot = $("#pb-rating"), b = badgeNode();
  if(slot && b) slot.appendChild(b);
}

/* The single condition that decides, everywhere on this page, whether
   Judge.me draws the ratings or the local fallback does.

   It has to be ONE function rather than the same checks written in
   three places: the badge in the buy column, the section further
   down and the JSON-LD must all agree, or the page shows a live star
   count beside a stale one and tells Google a third figure.

   ⚠ IT IS PER MODE, and it used to not be. It tested
   JUDGEME_PUBLIC_TOKEN unconditionally — a token that belongs to the
   "token" path alone and means nothing when the app is installed. On
   an app-mode store with the token still sitting in config.js that
   returned true, so the buy column drew an empty badge div and
   suppressed the local stars it was supposed to fall back to. */
function judgemeReady(){
  const mode = reviewsMode();
  if(mode === "local") return false;
  if(mode === "app")   return badgeReady();
  return Boolean(JUDGEME_PUBLIC_TOKEN) &&
         Boolean(product) &&
         Boolean(shopifyNumericId(product.id));
}

/* Judge.me's loader reads these off window before it boots, so they
   have to be set before the script tag is appended. Injected once —
   a second copy of the loader mounts every widget twice. */
let judgemeLoading = false;
function loadJudgeme(){
  if(judgemeLoading) return;
  judgemeLoading = true;

  window.jdgm = window.jdgm || {};
  /* Not DOMAIN. Judge.me may know this shop by its primary domain
     rather than its .myshopify one — see JUDGEME_SHOP_DOMAIN in
     config.js for how to tell which. */
  window.jdgm.SHOP_DOMAIN  = JUDGEME_SHOP_DOMAIN;
  window.jdgm.PUBLIC_TOKEN = JUDGEME_PUBLIC_TOKEN;
  /* "shopify" even though this page is not a Shopify theme. It tells
     Judge.me which account SHOP_DOMAIN belongs to, not where the
     page is being served from. */
  window.jdgm.PLATFORM = JUDGEME_PLATFORM;

  const s = document.createElement("script");
  s.src = `${JUDGEME_CDN}/widget_preloader.js`;
  s.defer = true;
  /* A blocked CDN, an ad blocker, or an offline phone. The widget is
     an enhancement over a section that already has content in it, so
     a failure repaints the fallback rather than leaving the
     customer looking at an empty heading. */
  s.addEventListener("error", () => {
    console.warn("[Caspian] Judge.me could not be loaded from " + JUDGEME_CDN +
      " — showing the local review counts instead.");
    renderLocalReviews();
  });
  document.head.appendChild(s);
}

function renderReviews(){
  const wrap = $("#reviews");
  if(!wrap) return;

  /* ---------- WHO DRAWS THE REVIEWS ----------
     Three answers, and the merchant picks one in the theme editor
     (Theme settings → Reviews). templates/product.liquid stamps the
     choice onto the section as data-reviews, so this file is told
     rather than left to guess.

       "app"    the Judge.me SHOPIFY APP draws them. The widget div
                is already in the page, written by Liquid, and the
                app embed loads the script that finds it. There is
                NOTHING for this function to do — and if it wrote a
                second widget the page would show the reviews TWICE,
                which is the real failure mode of a theme that
                carries a platform-independent embed onto Shopify.
                This is the default, and it needs no token.

       "token"  the platform-independent widget, loaded by this file
                from JUDGEME_PUBLIC_TOKEN. For a store where the app
                is not installed.

       "local"  no Judge.me at all — the recorded counts below.

     ⚠ "local" IS THE DEFAULT NOW, and it is no longer a fallback.
     It used to mean "a frozen count and no reviews"; it means the
     real published set — 18 reviews, 32 photographs, exported from
     Judge.me into assets/reviews-data.js and rendered from this
     page's own bundle. No app, no embed, no third-party script on
     the critical path of the page that sells the product.

     An unset attribute still falls through to the token path, which
     is what this file did before the setting existed. */
  const mode = reviewsMode();

  if(mode === "app"){
    /* Liquid drew the heading and the mount point; the app embed
       does the rest. Nothing to render and nothing to load. */
    return;
  }
  if(mode === "local") return renderLocalReviews();

  /* Everything Judge.me needs, or none of it. Each of these is a
     different fix and none of them should read as "reviews are
     broken", so they are reported separately. */
  if(!JUDGEME_PUBLIC_TOKEN){
    console.info("[Caspian] No Judge.me public token set — showing the local review " +
      "counts. Paste it into JUDGEME_PUBLIC_TOKEN in js/shop/config.js.");
    return renderLocalReviews();
  }
  if(!judgemeReady()){
    console.warn("[Caspian] Could not read a numeric Shopify product id" +
      (product ? ` from "${product.id}"` : " — Shopify did not answer") +
      ", so the Judge.me widget has nothing to key on. Showing local counts.");
    return renderLocalReviews();
  }

  const id = shopifyNumericId(product.id);

  /* Judge.me's own class names, exactly as their install snippet
     writes them — their script finds its mount points by these, so
     none of them can be renamed. data-id is the numeric product id;
     data-product-title is what their "write a review" form
     pre-fills.

     The heading is ours, so the section reads like the rest of the
     page rather than like an embed. */
  wrap.innerHTML = `
    <h2>What people say</h2>
    <div class="jdgm-widget jdgm-review-widget jdgm-outside-widget"
         data-id="${esc(id)}"
         data-product-title="${esc(product.title)}"></div>`;

  loadJudgeme();
}

/* The section as it was before Judge.me: real counts from the live
   listing at the time they were recorded, and no quotes. Kept whole
   because it is what a customer sees whenever the widget cannot
   load, and a degraded state that looks degraded costs a sale. */
/* THE REAL REVIEWS for the product this page is selling, or null.
   Only the Smart Mop has any; the three accessories have never been
   reviewed, and their section removes itself rather than printing a
   zero. */
const REVIEWS = aggregate(PRODUCT_REVIEWS[HANDLE] || null);

/* ⚠ THIS READS window.CASPIAN DIRECTLY AND MUST NOT BECOME AN IMPORT
   FROM theme-bridge.js. IT WAS ONE, FOR ABOUT AN HOUR, AND IT TOOK
   THE WHOLE PRODUCT PAGE DOWN.

   An `import "./theme-bridge.js"` specifier carries no ?v= — the
   browser resolves it against this file's directory and asks for the
   BARE asset URL. Shopify serves that with:

       cache-control: public, max-age=31557600

   a year. So when this file is pushed it gets a fresh versioned URL
   and runs immediately, while its import of theme-bridge.js is
   answered from a CDN copy that can be arbitrarily old. Add an export
   to a shared module and the new code asks the old file for it:

       SyntaxError: The requested module './theme-bridge.js'
       does not provide an export named 'reviewTotals'

   and a missing export is not a warning, it is a hard failure of the
   entire module graph. Nothing after it runs. The product page came
   back as a header and an empty <main>, because everything below the
   header is built by this file.

   THE RULE THAT FALLS OUT OF THAT: changing what a shared module
   DOES is safe — the stale copy is merely older behaviour. Changing
   what it EXPORTS is not, and there is no version string available
   at the import site to fix it with. Anything new goes in the page
   module, which is versioned, or through window.CASPIAN, which is
   re-rendered with every page.

   ────────────────────────────────────────────────────────────

   THE HEADLINE FIGURES vs THE CARDS BELOW THEM. These are two
   different sets of numbers and conflating them is the bug this
   exists to prevent.

   ⚠ assets/reviews-data.js CARRIES ONLY WHAT WAS IMPORTED INTO
   JUDGE.ME — the 18 rows of the CSV export, 17 of them pulled across
   from the Amazon listing. That is not how many people have reviewed
   the product. So `items` is a SUBSET, and counting it gives the size
   of that subset, not the total.

   (An earlier version of this note said the file held only the
   reviews WITH PHOTOGRAPHS and that bundling the rest would bloat the
   page. That was wrong — every row of the export is in there,
   including the ones with `"photos": []`. The conclusion survives the
   correction, which is why it is worth writing down: 18 is a subset
   because 18 is what was imported, not because of a filter.)

   The true totals are typed in the theme editor from the Judge.me
   dashboard. The average is DERIVED from the five counts, never
   typed, so the big figure and the bars beside it cannot disagree.

   ⚠ WHAT THIS MEANS ON THE PAGE, and it is deliberate: the score and
   the five bars describe all 181 reviews, while the cards underneath
   are the 18 there is text for. "Based on 181 reviews" is a claim
   about the SCORE, not a promise to print 181 cards. Do not "fix"
   this by making the bars count the cards — that would report 4.9
   from 18 and under-count the product by an order of magnitude.

   Number() everywhere because a theme setting can come back as a
   string, and "141" + 40 is "14140". */
function reviewTotals(){
  const t = (typeof window !== "undefined" && window.CASPIAN || {}).reviewTotals;
  if(!t) return null;

  const distribution = {};
  let count = 0, weighted = 0;
  for(const n of [5, 4, 3, 2, 1]){
    const c = Math.max(0, Math.round(Number(t[n]) || 0));
    distribution[n] = c;
    count += c;
    weighted += c * n;
  }
  if(!count) return null;

  // Two decimals with trailing zeros dropped: 4.7790… prints "4.78",
  // a clean 4.5 prints "4.5" rather than "4.50".
  return { average: Number((weighted / count).toFixed(2)), count, distribution };
}

/* ⚠ ONE FUNCTION, THREE CALLERS: the stars under the title, the
   section further down, and the JSON-LD Google reads. They used to
   each reach for PRODUCT_REVIEWS themselves, which is precisely how
   two halves of a page end up printing different numbers. */
function aggregate(base){
  if(!base) return base;
  const real = reviewTotals();
  if(!real) return base;
  return { ...base, ...real };
}

/* How many cards are drawn before "Show all". RAISED FROM 6 TO 12 on
   request — "show more" — so two thirds of the reviews are on the
   page without anyone having to press anything, and the button is
   now a short tail rather than most of the section.

   This costs nothing at load: all eighteen are already in the HTML
   string either way. The button only unhides the rest, so there is
   no second render and no jump.

   ⚠ THE CEILING IS THE NUMBER OF REAL REVIEWS. Eighteen is what the
   Judge.me export contains. Raising this past that shows eighteen
   and hides the button — it cannot conjure a nineteenth. */
const REVIEWS_SHOWN = 12;

/* "2025-06-28" as something a person reads. Guarded: a malformed
   date prints nothing rather than "Invalid Date". */
function reviewDate(iso){
  const d = new Date(iso + "T00:00:00");
  if(Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/* Where the review was left. Stated plainly and NOT dressed up:
   "Verified purchase" is a claim about a transaction this page has
   no way to check, and these came across in an export. Naming the
   platform is the honest version and it is also the more persuasive
   one. */
const SOURCE_LABEL = { amazon: "via Amazon", web: "via caspian.in" };

/* One flat list of every photograph in the section, in the order
   they are drawn, so the lightbox can step through the lot with the
   arrow keys instead of being trapped inside one review. Rebuilt on
   each render; the index in data-i is a position in THIS array. */
let reviewPhotos = [];

/* ⚠ THE FIRST FEW ARE NOT LAZY, AND THAT IS THE FIX FOR "the review
   photos load late".

   Every one of these is hotlinked to Amazon's CDN, so each is a fresh
   connection to a third-party host — DNS, TLS, then the file. With
   loading="lazy" on all of them the browser will not even START that
   until the strip is nearly in view, and the handshake it has been
   putting off happens exactly when the customer arrives. The result
   is a row of empty boxes that fill in one by one under the reader.

   The strip is the fastest read in the section — the product in
   somebody's kitchen, before a word — so the ones that open it are
   worth fetching early. The rest stay lazy: there are 32 of them and
   most are far down the page.

   Paired with a preconnect to m.media-amazon.com in
   layout/theme.liquid, which gets the handshake out of the way while
   the page is still parsing. */
function photoButton(p, i, cls, eager){
  return `<button type="button" class="${cls}" data-i="${i}"
            aria-label="Open customer photograph ${i + 1}">
    <img src="${esc(p.thumb)}" alt=""
         loading="${eager ? "eager" : "lazy"}"
         ${eager ? 'fetchpriority="high"' : ""} decoding="async"></button>`;
}

/* How many of the strip's photographs are fetched up front. Six fills
   a phone's visible row roughly twice over. */
const REVIEW_PHOTOS_EAGER = 6;

function renderLocalReviews(){
  const wrap = $("#reviews");
  if(!wrap) return;

  /* No reviews for this product — not "0 reviews", which reads as
     nobody liking it. The section is not there at all. */
  if(!REVIEWS?.count){ wrap.remove(); return; }

  const { average, count, distribution, items } = REVIEWS;

  /* 5 down to 1, always all five rows. A histogram that omits the
     empty ratings is not a histogram, it is a selection. */
  const bars = [5, 4, 3, 2, 1].map(n => {
    const c = distribution[n] || 0;
    const pct = count ? Math.round(c / count * 100) : 0;
    return `<div class="rv-bar">
      <i>${n}★</i><u><b style="width:${pct}%"></b></u><em>${c}</em>
    </div>`;
  }).join("");

  reviewPhotos = [];
  const cards = items.map((r, ri) => {
    const pics = r.photos.map(p => {
      reviewPhotos.push(p);
      return photoButton(p, reviewPhotos.length - 1, "rv-pic");
    }).join("");

    /* ⚠ AMAZON ORDER: WHO, THEN THE STARS, THEN WHAT THEY SAID.
       The name used to sit in a meta line under the card with the
       date and the source. It reads better at the top — a review is
       somebody's opinion, and the person comes before the opinion.

       The date and the "via Amazon" label are gone on request. Note
       what that does NOT change: these are still the same real
       reviews, exported from Judge.me. Dropping the label only
       removes where they were originally left, not who wrote them.

       ⚠ The name is a <cite> in the HEAD now, not a <footer> or a
       bare <div> at the bottom. sections.css styles bare footer{} —
       black ground, 34px rounded corners, up to 150px of padding —
       and an element selector reaches every <footer> on the page.
       Marking this up semantically once painted a black slab across
       the bottom of all eighteen cards with the name invisible in
       it. Do not reintroduce a <footer> here. */
    return `
      <article class="rv-card${ri >= REVIEWS_SHOWN ? " is-extra" : ""}">
        <div class="rv-card-head">
          <cite class="rv-who">${esc(r.name)}</cite>
          ${stars(r.rating)}
          ${r.title ? `<h3>${esc(r.title)}</h3>` : ""}
        </div>
        <p class="rv-body">${esc(r.body)}</p>
        ${pics ? `<div class="rv-pics">${pics}</div>` : ""}
      </article>`;
  }).join("");

  /* The strip along the top: every photograph in the section, in one
     scrollable row. It is the fastest read in the whole block — the
     product in somebody's actual kitchen, at a glance, before a word
     is read. The buttons reuse the same index as the ones inside the
     cards, so opening a photo from either place lands in the same
     place in the lightbox. */
  const strip = reviewPhotos.length
    ? `<div class="rv-strip" role="list" aria-label="Photographs from customers">
         ${reviewPhotos.map((p, i) => photoButton(p, i, "rv-shot", i < REVIEW_PHOTOS_EAGER)).join("")}
       </div>`
    : "";

  wrap.innerHTML = `
    <h2>What people say</h2>
    <div class="rv-top">
      <div class="rv-score">
        <strong>${average}</strong>
        ${stars(average)}
        <span>Based on ${count} review${count === 1 ? "" : "s"}</span>
      </div>
      <div class="rv-bars">${bars}</div>
    </div>
    ${strip}
    <div class="rv-list">${cards}</div>
    ${items.length > REVIEWS_SHOWN ? `
      <button type="button" class="btn btn--line rv-more" id="rv-more">
        <i class="btn-dot"></i>
        <!-- "Show more", not "Show all 18 with photos". Changed on
             request. The count is already stated twice above this
             button — in the score line and in the bars — so putting
             it a third time on the control added nothing, and "with
             photos" described the reviews rather than the action.
             data-label must stay identical to the visible span: it
             is what .swap reserves width against, and a mismatch
             makes the button resize on hover. -->
        <span class="swap" data-label="Show more">
          <span>Show more</span></span>
      </button>` : ""}

    <!-- The lightbox. One in the section rather than one per photo,
         built empty and filled on open — 32 hidden full-size <img>
         tags would be 32 requests the moment anything preloaded
         them. -->
    <div class="rv-box" id="rv-box" hidden role="dialog" aria-modal="true"
         aria-label="Customer photograph">
      <button type="button" class="rv-box-x" data-rv-close aria-label="Close">&times;</button>
      <button type="button" class="rv-box-p" data-rv-step="-1" aria-label="Previous photograph">&lsaquo;</button>
      <img id="rv-box-img" alt="Photograph from a customer review">
      <button type="button" class="rv-box-n" data-rv-step="1" aria-label="Next photograph">&rsaquo;</button>
      <span class="rv-box-n-of" id="rv-box-count"></span>
    </div>
  `;

  wireReviews();
}

/* Show-all, and the lightbox. Delegated from the section, so it
   survives the section being re-rendered and costs three listeners
   rather than one per photograph. */
function wireReviews(){
  const wrap = $("#reviews");
  const box  = $("#rv-box");
  const img  = $("#rv-box-img");
  const nOf  = $("#rv-box-count");
  if(!wrap || !box) return;

  let at = -1;

  const show = i => {
    if(!reviewPhotos.length) return;
    // wrap around, so the arrows never dead-end
    at = (i + reviewPhotos.length) % reviewPhotos.length;
    img.src = reviewPhotos[at].full;
    nOf.textContent = `${at + 1} / ${reviewPhotos.length}`;
    box.hidden = false;
    document.body.classList.add("rv-open");
  };

  const close = () => {
    box.hidden = true;
    // Drop the source: a 150KB image held in a hidden dialog is
    // 150KB of memory doing nothing on a phone.
    img.removeAttribute("src");
    document.body.classList.remove("rv-open");
  };

  wrap.addEventListener("click", e => {
    const shot = e.target.closest("[data-i]");
    if(shot){ show(Number(shot.dataset.i)); return; }

    const step = e.target.closest("[data-rv-step]");
    if(step){ show(at + Number(step.dataset.rvStep)); return; }

    // The backdrop and the × both close. Clicking the photograph
    // itself must not — that is the thing being looked at.
    if(e.target.closest("[data-rv-close]") || e.target === box){ close(); return; }

    if(e.target.closest("#rv-more")){
      wrap.classList.add("rv-all");
      $("#rv-more")?.remove();
    }
  });

  addEventListener("keydown", e => {
    if(box.hidden) return;
    if(e.key === "Escape")     close();
    if(e.key === "ArrowRight") show(at + 1);
    if(e.key === "ArrowLeft")  show(at - 1);
  });
}

/* FIVE QUESTIONS, and the cap is enforced HERE as well as in the
   list itself.

   The local list in product-data.js was cut to five about the mop,
   but that list is only the fallback — the Shopify metafield
   overrides it wholesale, and it is edited in an admin by someone
   who will not know about this decision. Without this line a
   seventeen-question metafield would quietly undo it.

   It takes the FIRST five, so the order in the metafield is the
   order of importance. */
const FAQ_MAX = 5;

function renderFaq(){
  const wrap = $("#faq");
  if(!wrap) return;
  if(!C.faq?.length){ $(".faq")?.remove(); return; }
  wrap.innerHTML = C.faq.slice(0, FAQ_MAX).map(f => `
    <details>
      <summary>${esc(f.q)}</summary>
      <p>${esc(f.a)}</p>
    </details>`).join("");
}

/* ---------- frequently bought together ----------
   The accessories, under the how-to-use steps: the pad, the bottle
   and the detergent, each with its own price and its own Add to
   cart.

   THE CATALOGUE, MINUS THIS PAGE'S PRODUCT. Not a hardcoded list of
   three handles — publish a fourth accessory in Shopify and it
   appears here, unpublish one and it goes, with no code change. The
   current product is filtered out for the obvious reason: "also
   buy the thing you are looking at" is not a recommendation.

   It is a SECOND network call, deliberately made late and
   deliberately allowed to fail: getCatalog() is awaited after the
   page has already rendered and sold the mop, and if it throws the
   section removes itself. Nothing here blocks the price. */
async function renderBought(){
  const wrap = $("#bought");
  if(!wrap) return;

  const drop = () => $(".pbought")?.remove();

  let catalog = [];
  try{
    catalog = await getCatalog();
  }catch(err){
    console.warn("[Caspian] Frequently-bought-together could not load:",
      err.code || "ERROR", err.detail || err.message);
    drop();
    return;
  }

  /* "Include this product" in the section settings puts the page's
     own product first, ahead of the consumables. */
  const self = wrap.hasAttribute("data-include-self")
    ? catalog.filter(p => p.handle === HANDLE) : [];
  const others = [...self, ...catalog.filter(p => p.handle !== HANDLE)];
  if(!others.length){ drop(); return; }

  wrap.innerHTML = others.map(p => {
    /* The variant that gets added: the first that can be bought,
       falling back to the first that exists so a sold-out accessory
       still shows its price rather than nothing. */
    const v = p.variants.nodes.find(x => x.availableForSale) || p.variants.nodes[0];
    if(!v) return "";

    const img  = p.featuredImage || p.images?.nodes?.[0] || null;
    const href = productPath(p.handle);
    const A  = href ? "a" : "div";
    const at = href ? ` href="${esc(href)}"` : "";

    return `
      <article class="fbt" data-handle="${esc(p.handle)}">
        <${A} class="fbt-shot"${at}${href ? ` tabindex="-1"` : ""}>
          ${v.availableForSale ? "" : `<span class="fbt-out">Sold out</span>`}
          ${img ? `<img src="${esc(sized(img.url, 420))}"
               srcset="${esc(srcset(img.url, W.card))}" sizes="${FBT_SIZES}"
               alt="${esc(img.altText || p.title)}"
               loading="lazy" decoding="async">` : ""}
        </${A}>
        <div class="fbt-body">
          <h3>${href ? `<a href="${esc(href)}">${esc(p.title)}</a>` : esc(p.title)}</h3>
          <p class="fbt-price">
            <span>${formatMoney(v.price)}</span>
            ${v.compareAtPrice ? `<span class="fbt-was">${formatMoney(v.compareAtPrice)}</span>` : ""}
          </p>
          <button type="button" class="btn btn--line fbt-add"
                  ${v.availableForSale ? "" : "disabled"}>
            <i class="btn-dot"></i>
            <span class="swap" data-label="${v.availableForSale ? "Add" : "Sold out"}"
              ><span>${v.availableForSale ? "Add" : "Sold out"}</span></span>
          </button>
        </div>
      </article>`;
  }).join("");

  /* Delegated, so it survives the markup having been written a line
     ago — and so there is one handler rather than one per card. */
  wrap.addEventListener("click", e => {
    const btn = e.target.closest(".fbt-add");
    if(!btn || btn.disabled) return;

    const handle = btn.closest(".fbt")?.dataset.handle;
    const p = others.find(x => x.handle === handle);
    if(!p) return;

    const v = p.variants.nodes.find(x => x.availableForSale) || p.variants.nodes[0];
    if(!v) return;

    addLine(v.id, 1, {
      handle: p.handle,
      title: p.title,
      variantTitle: v.title,
      price: v.price,
      image: p.featuredImage?.url ?? v.image?.url ?? ""
    });

    flashAdded(btn);
    notify(`Added ${p.title} to cart`);
    // Same frame as the click, rather than a listener tick later.
    paintCartState();
  });
}

/* The `sizes` hint for those cards — a four-up row on a desktop,
   two-up on a phone. Keep in step with .pbought-grid in
   assets/product.css. */
const FBT_SIZES = "(min-width: 980px) 300px, (min-width: 600px) 30vw, 44vw";


/* ---------- Caspian Ka Promise ----------
   The aftercare terms as their own section, built to the supplied
   artwork: the lockup, then a wide first term and two beneath it.

   THE GLYPHS ARE INLINE SVG, not the supplied raster. Three reasons
   and they all matter: they are ~400 bytes each against 50KB of PNG,
   they take `currentColor` so they cannot go out of step with the
   palette, and the tick inside two of them is a path that can be
   DRAWN on entry — see .promise-card.is-in in assets/product.css. A
   flat picture cannot do the last one.

   `stroke-dasharray` is set here in the markup rather than in CSS
   because the value is the path's own length, and these are short
   fixed paths whose lengths are known: getTotalLength() would mean
   measuring in script, which forces layout for an effect that is
   pure decoration. 40 comfortably exceeds every tick below, and an
   over-long dash simply finishes early. */
const PROMISE_ICONS = {
  shield: `
    <path class="promise-body" d="M12 3.2l7 2.6v5.5c0 4.2-2.9 7.6-7 9.5-4.1-1.9-7-5.3-7-9.5V5.8z"/>
    <path class="promise-tick" d="M8.6 12.2l2.5 2.5 4.6-5" stroke-dasharray="40" stroke-dashoffset="40"/>`,
  /* A rosette — a circle with a wavy edge. The outer path is twelve
     scallops written out rather than generated, because it is drawn
     once and a loop that emits path data is harder to read than the
     data. */
  badge: `
    <path class="promise-body" d="M12 2.6l1.9 1.4 2.3-.5 1.1 2.1 2.1 1.1-.5 2.3 1.4 1.9-1.4 1.9.5 2.3-2.1 1.1-1.1 2.1-2.3-.5-1.9 1.4-1.9-1.4-2.3.5-1.1-2.1-2.1-1.1.5-2.3L2.7 12l1.4-1.9-.5-2.3 2.1-1.1 1.1-2.1 2.3.5z"/>
    <circle class="promise-body" cx="12" cy="12" r="4.6"/>
    <path class="promise-tick" d="M9.9 12.1l1.6 1.6 2.8-3.2" stroke-dasharray="40" stroke-dashoffset="40"/>`,
  battery: `
    <rect class="promise-body" x="7" y="5.4" width="10" height="15.2" rx="2"/>
    <path class="promise-body" d="M10.2 5.4V4.3a1 1 0 011-1h1.6a1 1 0 011 1v1.1"/>
    <path class="promise-bolt" d="M12.6 8.6l-2.3 4.3h1.8l-.7 3.5 2.6-4.6h-2z"/>`
};

function renderPromise(){
  const wrap = $("#promise");
  if(!wrap) return;

  const p = C.promise;
  // Accessories have no promise block; the section goes rather than
  // standing empty under a heading.
  if(!p?.items?.length){ $(".promise")?.remove(); return; }

  /* THE LABEL IS SET ON TWO LINES, exactly as the artwork has it:

       "10 day replacement"  ->  10 DAY
                                 REPLACEMENT
       "3 months warranty"   ->  3 MONTHS
                                 WARRANTY

     So it splits three ways: the leading number, the word after it,
     and everything left. All three terms happen to fit that shape,
     which is not a coincidence — it is how the copy was written.

     A label that does not match — no leading number, or only two
     words — degrades rather than breaks: whatever is missing is
     simply not emitted, and the line that is left still renders. */
  const card = (item, i) => {
    const m = /^(\d+)\s+(\S+)\s*(.*)$/.exec(item.label ?? "");
    const num  = m ? m[1] : "";
    const unit = m ? m[2] : (item.label ?? "");
    const rest = m ? m[3] : "";

    return `
      <div class="promise-card promise-card--${esc(item.tint || "green")}"
           style="--i:${i}">
        <div class="promise-ico">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            ${PROMISE_ICONS[item.icon] ?? PROMISE_ICONS.shield}
          </svg>
        </div>
        <div class="promise-txt">
          <p class="promise-label">
            <span class="promise-l1">
              ${num ? `<b>${esc(num)}</b>` : ""}${unit ? `<i>${esc(unit)}</i>` : ""}
            </span>
            ${rest ? `<span class="promise-l2">${esc(rest)}</span>` : ""}
          </p>
          <p class="promise-detail">${esc(item.detail)}</p>
        </div>
      </div>`;
  };

  wrap.innerHTML = `
    <div class="promise-head">
      ${p.eyebrow ? `<p class="promise-eyebrow">${esc(p.eyebrow)}</p>` : ""}
      <h2 class="promise-title">${esc(p.heading || p.title)}</h2>
    </div>
    <div class="promise-grid">
      ${p.items.map(card).join("")}
    </div>`;

  /* The accessible name of the section is the whole phrase — "Caspian
     Ka Promise" — even though the heading on screen is split in two
     for the lockup. A screen reader announcing a region called
     "Promise" has been told almost nothing. */
  wrap.closest(".promise")?.setAttribute("aria-label", p.title || "Our promise");

  /* ---- the entrance ----
     An IntersectionObserver rather than a ScrollTrigger, so the
     section animates even when the GSAP CDN never answered. It is
     the only beat on this page that does not depend on it, and that
     is deliberate: everything else here is a tween on top of content
     that is already correct, whereas the tick DRAWING is the whole
     character of this section.

     .is-in is a one-way switch and the observer disconnects — a
     warranty panel that replays every time you scroll past reads as
     a page that cannot settle. */
  const section = wrap.closest(".promise");
  if(!section) return;

  if(!("IntersectionObserver" in window) ||
     matchMedia("(prefers-reduced-motion:reduce)").matches){
    section.classList.add("is-in");
    return;
  }

  const io = new IntersectionObserver(entries => {
    for(const e of entries) if(e.isIntersecting){
      e.target.classList.add("is-in");
      io.disconnect();
    }
  }, { rootMargin: "0px 0px -12% 0px" });

  io.observe(section);
}


/* ---------- assistance & support ----------
   A named person, a phone number and a WhatsApp thread. Both
   buttons are real links — tel: and wa.me — so they work on a phone
   without a line of JavaScript, which is the only place they matter.

   THE PHOTOGRAPH IS OPTIONAL. If the file is not in the repo the
   <img> is removed on its error event and the monogram underneath
   is what shows, so this panel is never a broken image icon. See
   the README in assets/support/. */
function renderSupport(){
  const wrap = $("#support");
  if(!wrap) return;

  const s = C.support;
  if(!s){ $(".psupport")?.remove(); return; }

  const a = s.agent ?? {};
  const wa = s.whatsapp
    ? `https://wa.me/${encodeURIComponent(s.whatsapp)}` +
      (s.whatsappText ? `?text=${encodeURIComponent(s.whatsappText)}` : "")
    : null;

  wrap.innerHTML = `
    <div class="psupport-card">
      <div class="psupport-face">
        <span class="psupport-mono" aria-hidden="true">${esc((a.name || "C").slice(0, 1))}</span>
        ${a.photo ? `<img src="${esc(a.photo)}"
             alt="${esc(a.name ? `${a.name}, Caspian customer support` : "Caspian customer support")}"
             loading="lazy" decoding="async">` : ""}
      </div>
      ${a.name ? `<p class="psupport-name">${esc(a.name)}</p>` : ""}
      <p class="psupport-line">${esc(s.line)}</p>

      <div class="psupport-act">
        <a class="btn psupport-call" href="tel:${esc(s.phone)}">
          <svg class="psupport-i" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6.6 10.8a15.1 15.1 0 006.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 013 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z"/>
          </svg>
          <span class="swap" data-label="Call us ${esc(s.phoneDisplay)}"
            ><span>Call us ${esc(s.phoneDisplay)}</span></span>
        </a>
        ${wa ? `<a class="btn psupport-wa" href="${esc(wa)}"
             target="_blank" rel="noopener noreferrer">
          <svg class="psupport-i" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2a10 10 0 00-8.7 15L2 22l5.2-1.3A10 10 0 1012 2zm0 18.2a8.2 8.2 0 01-4.2-1.2l-.3-.2-3.1.8.8-3-.2-.3A8.2 8.2 0 1112 20.2zm4.6-6.1c-.3-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1-.3.2-.6.1a6.7 6.7 0 01-3.3-2.9c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 00-.7.3A2.9 2.9 0 006 9.9c0 1.7 1.3 3.4 1.4 3.6a11.3 11.3 0 004.4 3.9c1.6.6 2.2.7 3 .5.5-.1 1.5-.6 1.7-1.2s.2-1.1.2-1.2-.2-.2-.5-.3z"/>
          </svg>
          <span class="swap" data-label="Chat on WhatsApp"><span>Chat on WhatsApp</span></span>
        </a>` : ""}
      </div>
    </div>`;

  /* No photograph in the repo yet, or a bad path: drop the <img> and
     let the monogram behind it show. `once` — a second error on the
     same node is not possible, and this must not linger. */
  const photo = wrap.querySelector(".psupport-face img");
  photo?.addEventListener("error", () => photo.remove(), { once: true });
}


/* ---------- newsletter ----------
   ⚠ IT DOES NOT PRETEND. NEWSLETTER_ENDPOINT in assets/config.js is
   empty until someone connects a real list, and while it is empty
   this form validates the address and then says, plainly, that the
   list is not connected yet — and offers an email link instead. A
   form that thanks somebody for subscribing to nothing has taken
   their address and given them a false belief, which is worse than
   having no form.

   Set the endpoint and everything below starts posting to it with
   no other change.

   `no-cors` on the fetch: most hosted form endpoints do not send
   CORS headers back, and without it the browser rejects a request
   the service actually received. The cost is that the response is
   opaque — we cannot read a status — so a network-level failure is
   the only failure this can report. That is the trade every
   third-party form embed makes. */
function renderNewsletter(){
  const form = $("#newsletter-form");
  if(!form) return;

  const input = $("#newsletter-email");
  const note  = $("#newsletter-note");
  const say = (msg, ok) => {
    if(!note) return;
    note.textContent = msg;
    note.classList.toggle("is-ok", !!ok);
    note.classList.toggle("is-bad", ok === false);
  };

  form.addEventListener("submit", async e => {
    e.preventDefault();

    const email = (input?.value ?? "").trim();
    // The browser's own type=email check, asked for explicitly so the
    // message is ours and appears in the same place as every other.
    if(!input?.checkValidity() || !email){
      say("That doesn't look like an email address.", false);
      input?.focus();
      return;
    }

    if(!NEWSLETTER_ENDPOINT){
      console.warn("[Caspian] NEWSLETTER_ENDPOINT is not set in assets/config.js — " +
        "the signup form has nowhere to send addresses.");
      say("Our mailing list isn't connected yet. Email support@smartmop.in " +
          "and we'll add you by hand.", false);
      return;
    }

    const btn = form.querySelector("button[type=submit]");
    if(btn) btn.disabled = true;
    say("Signing you up…");

    try{
      const body = new FormData();
      body.append(NEWSLETTER_FIELD, email);
      await fetch(NEWSLETTER_ENDPOINT, { method: "POST", mode: "no-cors", body });
      form.reset();
      say("You're on the list. Thanks!", true);
    }catch(err){
      console.error("[Caspian] Newsletter signup failed:", err);
      say("That didn't send. Please try again, or email support@smartmop.in.", false);
    }finally{
      if(btn) btn.disabled = false;
    }
  });
}

/* Product structured data, generated from the same object that drew
   the page — so the price Google reads can never disagree with the
   price on screen. */
function renderSchema(){
  const data = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.title,
    description: product.description,
    sku: current.sku,
    brand: { "@type": "Brand", name: product.vendor },
    offers: {
      "@type": "Offer",
      price: current.price.amount,
      priceCurrency: current.price.currencyCode,
      availability: current.availableForSale
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock"
    }
  };
  /* THE RATING, AND THE REVIEWS THEMSELVES.

     ⚠ NOT WHEN JUDGE.ME IS LIVE. Their widget emits its own rating
     markup; two aggregateRating blocks on one page is a structured
     data conflict, and the one Google would read out of this repo is
     the one guaranteed to go stale. So this is stated only when the
     theme is the thing drawing the reviews — which is now the
     default.

     The figures are the SAME OBJECT the section renders from, so the
     page and the structured data cannot disagree. Individual reviews
     are included as well as the average: they are real, attributed
     and dated, which is what makes them eligible in the first place,
     and a rating with no reviews behind it is the shape Google has
     been discounting. Capped at 12 — the whole set would roughly
     double the size of this tag for no additional eligibility. */
  const rv = aggregate(PRODUCT_REVIEWS[HANDLE] || C.reviews);
  if(!judgemeReady() && rv?.count){
    data.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rv.average,
      reviewCount: rv.count
    };
    if(rv.items?.length) data.review = rv.items.slice(0, 12).map(r => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      author: { "@type": "Person", name: r.name },
      datePublished: r.date,
      name: r.title || undefined,
      reviewBody: r.body
    }));
  }

  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}


/* ============================================================
   2. INTERACTION
   ============================================================ */

function wireBuy(){
  const input = $("#qty");
  const note  = $("#note");

  const setQty = n => {
    qty = Math.min(10, Math.max(1, n || 1));
    input.value = qty;
    paintBuybar();   // the bar states the quantity, so it has to follow
  };
  $("#qty-down").addEventListener("click", () => setQty(qty - 1));
  $("#qty-up").addEventListener("click",   () => setQty(qty + 1));
  input.addEventListener("change", () => setQty(parseInt(input.value, 10)));

  $("#add").addEventListener("click", () => {
    if(addToCart(note) === false) return;
    // No callback: this button never becomes anything else. #view-cart
    // appears beside it, so there is nothing waiting on the flash.
    flashAdded($("#add"));
  });

  /* Copy the code. The clipboard API needs a secure context, and a
     phone opened over plain http is not one — so a failure falls back
     to selecting the text, which the customer can then copy by hand
     rather than being told nothing happened. */
  const code = $("#coupon-code");
  code?.addEventListener("click", async () => {   // no coupon, no button
    const label = code.querySelector("i");
    try{
      await navigator.clipboard.writeText(C.coupon.code);
      label.textContent = "COPIED";
      setTimeout(() => { label.textContent = "COPY"; }, 1800);
    }catch{
      const r = document.createRange();
      r.selectNodeContents(code.querySelector("span"));
      getSelection().removeAllRanges();
      getSelection().addRange(r);
    }
  });

  document.querySelectorAll(".pb-swatch").forEach(b => {
    b.addEventListener("click", () => {
      const v = variantFor(b.dataset.option, b.dataset.value);
      if(!v) return;
      current = v;
      renderBuy();       // price, badge and availability all follow the variant
      renderGallery();
      paintBuybar();
      /* The buybar now tracks whether THIS variant is in the cart, so
         switching variant can change the answer: the Polyester pad is
         in the basket, the Microfibre one is not. Without this the bar
         kept the previous variant's verdict. */
      paintCartState();
    });
  });
}

/* ---------- the "it went in" moment ----------
   Adding to a cart on a phone is a tap with almost no consequence on
   screen: the note lives up in the buy column, the header badge is a
   few pixels in a corner. People tap twice because they cannot tell
   whether the first one landed.

   So the button says so itself, for 1.2s, using the machinery it
   already has — the cyan fill wipes up and the label swaps, exactly
   as a hover does. Nothing new is invented; is-added just holds the
   hover pose and swaps the incoming word for "Added". The one added
   part is a single scale pop, because under a thumb the fill alone
   is easy to miss.

   `then` fires when it is over, which is how the buybar knows when
   it is safe to turn into View cart.

   Re-entrant on purpose: tap it three times and the timer restarts
   rather than three of them racing to restore the label. */
const ADDED_MS = 1200;

/* ---------- the toast ----------
   The pill itself moved to assets/toast.js when the home page cards
   grew an Add to cart of their own — two copies of a confirmation
   are two things to keep in step. Only the one page-specific fact
   stays here: whether the sticky buy bar is up, so the pill can sit
   clear of it rather than covering the button that spawned it. */
const notify = msg => toast(msg, {
  raised: !!$("#buybar")?.classList.contains("is-on")
});

function flashAdded(btn, then){
  if(!btn){ then?.(); return; }

  const swap = btn.querySelector(".swap");
  // Stash the real label on the node the first time only — a second
  // tap mid-flash must not memorise "Added" as the thing to restore.
  if(swap && btn._label == null) btn._label = swap.dataset.label;
  if(swap) swap.dataset.label = "Added ✓";

  btn.classList.add("is-added");
  clearTimeout(btn._addedT);

  btn._addedT = setTimeout(() => {
    btn.classList.remove("is-added");
    if(swap && btn._label != null){ swap.dataset.label = btn._label; btn._label = null; }
    then?.();
  }, ADDED_MS);
}

function addToCart(note){
  /* The deal picker's "Mop only" card while that variant does not exist
     in Shopify yet: refuse rather than add the kit variant at its price
     under the cheaper card. See renderDeal(). */
  if(dealOn() && dealIsSolo() && !soloVariant()){
    if(note) note.textContent = "Mop only isn't available to order yet. Pick the deal with the free kit.";
    return false;
  }
  /* current.id is now a REAL Shopify variant gid, straight from the
     API — which is exactly what cartLinesAdd will want when checkout
     is wired in. The snapshot beside it is for drawing the cart
     page without re-fetching; Shopify remains the authority.

     Both image fields are nullable, hence the chain: a line with no
     picture is fine, a line whose image is the string "undefined"
     is a broken <img> on the cart page. */
  addLine(current.id, qty, {
    // The handle travels with the line so the cart page can link a
    // row back to the right product. Without it every row pointed
    // at the Smart Mop, which was correct while it was the only
    // thing for sale and is wrong now that there are four.
    handle: product.handle,
    title: product.title,
    variantTitle: current.title,
    price: current.price,
    image: product.featuredImage?.url ?? current.image?.url ?? ""
  });

  /* ---------- Meta Pixel — AddToCart ----------

     `window.fbq?.(...)`, and the optional call is the whole point.
     fbq only exists if the base tag ran, and the base tag only runs
     if a Pixel ID is set in theme settings AND connect.facebook.net
     was reachable — a content blocker, a privacy extension or a
     network that filters it all leave fbq undefined. A bare fbq()
     there would throw, and it would throw BEFORE the toast and
     before paintCartState(): an ad tag silently breaking the shop
     for exactly the visitors who block ad tags. It does not get to
     do that.

     content_ids carries the HANDLE, matching what
     snippets/meta-pixel.liquid sends for ViewContent. The two must
     agree — send an id from one and a handle from the other and Meta
     treats them as different products, so the funnel never joins up.

     value is the LINE total, qty included, because that is what was
     actually put in the basket. */
  window.fbq?.("track", "AddToCart", {
    content_ids: [product.handle],
    content_type: "product",
    content_name: product.title,
    value: Number(current.price?.amount ?? 0) * qty,
    currency: current.price?.currencyCode ?? "INR",
    contents: [{ id: product.handle, quantity: qty }]
  });

  if(note){
    /* No longer "remember code FREEDOM at checkout" — it is an
       automatic discount and there is nothing for the customer to
       remember. Telling them to do something Shopify already does
       invites them to go looking for a field that is not there. */
    note.textContent = C.coupon
      ? `Added ${qty} to your cart. The ${C.coupon.headline.toLowerCase()} comes off at checkout.`
      : `Added ${qty} to your cart.`;
    note.classList.add("is-ok");
  }

  /* One toast for both buttons, so the confirmation is identical
     whether the tap came from the buy column or the sticky bar.
     "Default Title" is Shopify's placeholder for a product with no
     real options — printing it would be worse than saying nothing. */
  const variant = current.title && current.title !== "Default Title"
    ? ` (${current.title})` : "";
  notify(`Added ${qty > 1 ? qty + " × " : ""}${product.title}${variant} to cart`);

  // Reveals both View cart controls. onCartChange would do this on
  // its own — the call is here so it happens in the same frame as
  // the click rather than a listener tick later.
  paintCartState();

  /* Checkout is Shopify's own, reached from the cart page (see
     checkout.js). Nothing on this page leaves for smartmop.in;
     this is Caspian's own storefront now. */
}

/* The header — and with it the cart count — is built by
   js/modules/header.js and subscribes to the cart itself. This page
   used to paint that badge by hand; it does not need to any more. */

/* Is THIS variant already in the basket? Not "is there a cart" —
   the difference is the whole bug below. */
function inCart(id){
  // The free gift line does not count: on the kit's own page, a free
  // kit in the cart is not the customer having bought one.
  return !!id && getCart().lines.some(l => l.merchandiseId === id && !l.gift);
}

/* Set while the Added confirmation is playing, so the swap waits for
   it to finish instead of yanking the button out from under it. */
let holdBuybar = false;

/* ---------- "there is a cart now" ----------
   The two View cart controls answer DIFFERENT questions, and used to
   answer the same one.

   #view-cart sits BESIDE the real Add to cart, up the page. It asks
   "is there a cart" — global — because it takes nothing away: Add to
   cart is still right there next to it.

   The buybar REPLACES its button, so the same rule broke the page.
   Add one product, walk to the next product, and its sticky bar came
   up already showing View cart — because the cart was not empty. The
   Add to cart button for the product you were actually looking at
   was never rendered, and the bar offered no way to buy the thing on
   screen. So the bar asks the narrower question: is THIS variant in
   the cart. Every product starts on Add to cart and earns its way to
   View cart, which is also the order a customer expects to see.

   Still cart-driven rather than click-driven, so emptying the basket
   in another tab puts the button back. */
function paintCartState(){
  const view = $("#view-cart");
  if(view) view.hidden = lineCount() === 0;

  if(holdBuybar) return;           // mid-confirmation; the callback repaints
  const mine = inCart(current?.id);
  const bAdd = $("#buybar-add"), bView = $("#buybar-view");
  if(bAdd)  bAdd.hidden  = mine;
  if(bView){
    bView.hidden = !mine;
    // Emptying the cart elsewhere puts Add to cart back. Drop the
    // one-shot class with it, or a later reveal that nobody tapped
    // for would replay the entrance.
    if(!mine) bView.classList.remove("is-fresh");
  }
}
onCartChange(paintCartState);

/* ---------- mobile buy bar ----------
   Shown once the real button has left the screen, so it never covers
   the thing it duplicates. */
function paintBuybar(){
  const now = $("#buybar-now"), was = $("#buybar-was"), sub = $("#buybar-sub");

  /* ⚠ THE BAR LEADS WITH THE PRICE THE CUSTOMER PAYS, not with the
     list price. It used to state current.price big and hide the
     couponed figure in 10px underneath — which put the LARGER of
     the two numbers in the loudest position and the one that
     actually gets charged in the quietest, on the one control that
     is on screen at the moment of deciding.

     So: landed price big and green, list price struck beside it.
     Same pair as the home page card, same colours, same order.

     This is only honest because FREEDOM applies itself — see the note
     over `coupon` in assets/product-data.js. A coupon the customer
     has to type could not be led with here. */
  const landed = couponMoney();

  if(now){
    now.textContent = formatMoney(landed ?? current.price);
    now.classList.toggle("is-deal", !!landed);
  }
  /* Struck only when there IS a discount to strike. A product with
     no coupon shows one price and no ghost of a second. */
  if(was){
    was.textContent = landed ? formatMoney(current.price) : "";
    was.hidden = !landed;
  }
  if(!sub) return;

  /* The quantity stepper is off screen whenever this bar is up — that
     is the condition that reveals it. So a quantity above one has to be
     stated HERE. Without this, tapping Add puts three in the cart and
     nothing on screen ever said three.

     The coupon’s CODE still belongs on this line — the price it
     buys has moved up to the headline, so what is left to say is
     which code did it. With no coupon and one item there is nothing
     to state and the row stays empty rather than blank-but-present. */
  paintBuybarDeal();
  const deal = dealOn()
    ? (dealIsSolo() ? "Mop only" : "With free cleaning kit")
    : C.coupon ? `with ${C.coupon.code}` : "";
  sub.textContent = qty > 1 ? (deal ? `Qty ${qty} · ${deal}` : `Qty ${qty}`) : deal;
}

function initBuybar(){
  const bar = $("#buybar"), anchor = $("#add");
  if(!bar || !anchor) return;
  paintBuybar();
  paintCartState();

  $("#buybar-add").addEventListener("click", () => {
    /* Hold the swap BEFORE adding: addToCart calls paintCartState in
       the same frame, and without this the button would vanish into
       View cart on the first frame of its own confirmation. */
    if(dealOn() && dealIsSolo() && !soloVariant()){ addToCart($("#note")); $("#buy")?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    holdBuybar = true;
    addToCart($("#note"));

    // The confirmation lives up in the buy column, which is off screen
    // at this point — so the bar reports for itself, and says how many.
    const sub = $("#buybar-sub");
    sub.textContent = `Added ${qty} to cart`;

    flashAdded($("#buybar-add"), () => {
      holdBuybar = false;
      // Fresh only on this path, so the reveal animates after a tap
      // and not on every load of a page whose product is already in.
      $("#buybar-view")?.classList.add("is-fresh");
      paintCartState();   // Add to cart → View cart, now that it is safe
      paintBuybar();      // and the price line drops "Added n to cart"
    });
  });

  new IntersectionObserver(([e]) => {
    bar.classList.toggle("is-on", !e.isIntersecting);
  }, { threshold: 0 }).observe(anchor);
}


/* ---------- the commerce half could not load ----------
   Shown in place of the gallery and the buy column, and nowhere
   else. The rest of the page is local content and renders normally
   underneath it, so this reads as one part of the page being
   unavailable rather than the site being broken.

   ⚠ NO PRICE APPEARS HERE, and none ever should. There is no
   "last known price", no cached figure, no number from the repo.
   If Shopify cannot be reached, this site does not know what the
   Smart Mop costs, and it says so and gives a phone number. */
function renderCommerceError(err){
  const wrap = $("#buy");
  const gallery = $(".pdp-gallery");
  const support = C?.support;

  // The empty gallery frame would be a black box with nothing in it.
  if(gallery) gallery.remove();
  // Nothing to buy means nothing for the sticky buy bar to state.
  $("#buybar")?.remove();

  const isConfig = err?.code === "CONFIG";

  if(wrap) wrap.innerHTML = `
    <div class="shop-error" role="alert">
      <p class="shop-error-h">${isConfig
        ? "This store is not connected yet."
        : "We couldn't load live pricing right now."}</p>
      <p class="shop-error-p">${isConfig
        ? "The Shopify storefront token has not been set for this site."
        : "The price, the photos and what's in stock all come straight from our store, " +
          "and we couldn't reach it just now. Nothing below has changed."}</p>
      <div class="shop-error-a">
        <button type="button" class="btn btn--buy" id="shop-retry">
          <i class="btn-dot"></i>
          <span class="swap" data-label="Try again"><span>Try again</span></span>
        </button>
        ${support ? `<a class="btn btn--line" href="tel:${esc(support.phone)}">
          <i class="btn-dot"></i>
          <span class="swap" data-label="${esc(support.phoneDisplay)}"><span>${esc(support.phoneDisplay)}</span></span>
        </a>` : ""}
      </div>
      <p class="shop-error-c">Reference: ${esc(err?.code || "ERROR")}</p>
    </div>`;

  /* A reload, not a re-fetch: the failure happened during module
     evaluation, so there is no clean way to re-run it in place —
     and a full reload is what a customer means by "try again"
     anyway. clearCache first, so a retry cannot be answered by
     whatever was in the session. */
  $("#shop-retry")?.addEventListener("click", () => {
    clearCache();
    location.reload();
  });
}


/* ---------- watch demo ----------
   The button is in the markup (sections/main-product.liquid), not
   built here, because it is a fixed control with fixed copy and a
   merchant can read it there. This only gives it behaviour.

   THE WHOLE POINT OF THIS FUNCTION IS THAT THE FILM IS NOT
   DOWNLOADED UNTIL IT IS ASKED FOR. how-steps.mp4 is ~5MB and it is
   already on this page once, in the steps section. A <video src>
   sitting in a hidden dialog would be a second copy of it, fetched
   on every product page view by everyone who never presses the
   button — so the URL rides on the button as data-src and is only
   promoted to a real src on open, then removed again on close.
   load() after removing the attribute is what actually releases the
   buffer; without it the element keeps what it has already pulled.
   The review lightbox drops its image for the same reason. */
function wireDemo(){
  const open = $("#pdemo-open");
  const box  = $("#pdemo-box");
  const vid  = $("#pdemo-video");
  if(!open || !box || !vid) return;

  /* Where focus goes back to on close. A dialog that returns focus to
     the top of the document makes a keyboard user tab through the
     whole page again to get back to where they were. */
  const show = () => {
    vid.src = open.dataset.src;
    if(open.dataset.poster) vid.poster = open.dataset.poster;
    box.hidden = false;
    document.body.classList.add("pdemo-open");
    /* Autoplay WITH sound is refused by every browser, and this film
       is narrated — muting it to force a start would be worse than
       not starting. play() is attempted and its rejection swallowed:
       the controls are there, and a poster with a play button is a
       perfectly good resting state. */
    vid.play().catch(() => {});
    box.querySelector("[data-pdemo-close]")?.focus();
  };

  const close = () => {
    box.hidden = true;
    document.body.classList.remove("pdemo-open");
    vid.pause();
    vid.removeAttribute("src");
    vid.load();                 // releases what was already buffered
    open.focus();
  };

  open.addEventListener("click", show);

  /* The backdrop and the × both close; the film itself must not —
     that is the thing being watched, and its own controls are on it. */
  box.addEventListener("click", e => {
    if(e.target.closest("[data-pdemo-close]") || e.target === box) close();
  });

  addEventListener("keydown", e => {
    if(!box.hidden && e.key === "Escape") close();
  });
}


/* ---------- offer countdown ----------
   ⚠ THERE IS NO STORED DURATION HERE, AND THAT IS THE FEATURE. The
   ordinary version of this widget keeps "90 minutes" somewhere and
   starts it on load, so it reads 1:30:00 to every visitor forever and
   a refresh puts it straight back to the top. That is a deadline
   nobody has to meet, and it is discovered by anyone who reloads.

   This reads the clock instead. The day is cut into fixed windows of
   `cycle` seconds measured from midnight IST, and what is shown is
   the time left in the current one:

       left = cycle - ((now + anchor) mod cycle)

   so it is the same figure on every device at a given moment, a
   reload does not change it, and the loop happens by itself — when
   one window runs out the modulo lands back at the top of the next.

   Liquid renders the opening figures too, so nothing flashes zero.
   This recomputes immediately on load rather than waiting a second
   for the first tick: Shopify can serve a cached copy of the page,
   which would make the server-rendered figure stale by however long
   it sat in the cache. */
function startOffer(){
  const card = $("#offer");
  if(!card) return;

  const h = $("#offer-h"), m = $("#offer-m"), s = $("#offer-s");
  if(!h || !m || !s) return;

  const cycle  = Number(card.dataset.cycle)  || 6 * 3600;
  const anchor = Number(card.dataset.anchor) || 0;
  if(cycle <= 0) return;

  const pad = n => String(n).padStart(2, "0");

  const tick = () => {
    const now  = Math.floor(Date.now() / 1000) + anchor;
    /* The remainder is 0 exactly on a boundary, and `cycle - 0` is a
       full window rather than nothing — which is right: a boundary IS
       the start of the next window, not a moment of zero. */
    const left = cycle - (now % cycle);

    h.textContent = pad(Math.floor(left / 3600));
    m.textContent = pad(Math.floor(left % 3600 / 60));
    s.textContent = pad(left % 60);
  };

  tick();
  /* A plain interval, and it is allowed to drift: it is re-derived
     from Date.now() every time, so a tab that was suspended for an
     hour shows the right figure on its first tick back rather than
     resuming an hour behind. */
  setInterval(tick, 1000);
}


/* ============================================================
   3. GO
   ============================================================ */

/* The header first: it is the topmost thing on the page and it owns
   the cart badge, so it should not appear a beat after the content
   underneath it — and it must not wait on Shopify, which is why it
   is here and not above the fetch. */
/* Isolated. initHeader() builds the nav, the drawer and the cart
   badge — all DOM work — and a throw anywhere in it used to abort
   this module before it drew the page. A broken nav is a nuisance; a
   page that never renders is a lost sale. */
try{ initHeader(); }catch(err){ console.error("[Caspian] The header failed to build:", err); }

/* The commerce half. Everything in this block reads a price, a
   variant or an image, so all of it needs Shopify to have answered. */
if(product){
  renderGallery();
  renderBuy();
  renderSchema();     // JSON-LD, built from the same object that drew the page
}else{
  renderCommerceError(loadError);
}

/* The content half. None of this touches Shopify — it is the local
   content object — so it renders whether the store answered or not.

   renderClaim is first because it is the first thing on the page. It
   is content, not commerce: the claim card is on screen even when
   the store is unreachable and the buy column is an error. */
renderClaim();
renderSteps();
renderStats();
renderSpecs();
renderReviews();
/* renderPromise() was called here. Caspian Ka Promise is static
   markup in sections/product-promise.liquid now, rebuilt to the
   supplied design — see the note over that section. The function is left in
   this file, unused, because it is the only thing that knows how to
   draw the tint discs and the drawing tick, and that is worth
   keeping if the section ever goes back to being generated. */
renderSupport();
renderFaq();
renderNewsletter();

/* Both of these wire up markup Liquid already rendered, so they are
   content rather than commerce and run even when the store could not
   be reached — a countdown that vanishes because a price lookup
   failed would be a second fault reported as one. */
wireDemo();
startOffer();

/* The one renderer that reads the CATALOGUE rather than this page’s
   own product: frequently-bought-together. Not awaited — the page
   above is already complete and purchasable, and a slow read must
   not hold up anything. The section removes itself if it fails.

   ScrollTrigger.refresh when it lands, because three cards appearing
   halfway down the page move every start position under them. The
   optional chain is for the case where GSAP never arrived — the
   cards still render and still add to cart. */
renderBought().then(() => window.ScrollTrigger?.refresh());

/* Anything a home page section dropped into this page needs filling
   in: the FAQ accordion, the partner tracks, the charge callout, the
   Smart Mop teaser card, the voice note's transport.

   ⚠ ABOVE THE GSAP GUARD, deliberately, and for the same reason
   main.js keeps its copy above the guard: the teaser card carries a
   price and an Add to cart. Content must never be downstream of a
   CDN. The scroll beats for those sections are the other half of
   this and they DO sit below the guard, where decoration belongs.

   Not awaited — the catalogue read inside it must not hold up the
   buy column, which is already rendered and purchasable above.
   Refresh when it lands, because a card appearing mid-page moves
   every start position under it. */
initHomeContent().then(() => window.ScrollTrigger?.refresh());

if(product) initBuybar();

// Videos are plain DOM behaviour, no animation library involved, so
// they run regardless of whether GSAP arrived.
initVideo();
initSound();


/* ---------- decoration ----------
   Everything past this point is optional. If the CDN did not deliver,
   the page above is already complete and purchasable. */
const { gsap, ScrollTrigger, Lenis } = window;

if(gsap && ScrollTrigger){
  gsap.registerPlugin(ScrollTrigger);
  gsap.config({ force3D: true });
  ScrollTrigger.config({ ignoreMobileResize: true });

  const REDUCED = matchMedia("(prefers-reduced-motion:reduce)").matches;

  if(Lenis && !REDUCED){
    const lenis = new Lenis({
      duration: 1.15,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.7
    });
    // Published so the side menu can park the scroll loop while the
    // drawer is open — overflow:hidden alone would leave Lenis
    // ticking against a page that cannot move.
    window.lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);

    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener("click", e => {
        const target = document.querySelector(a.getAttribute("href"));
        if(!target) return;
        e.preventDefault();
        lenis.scrollTo(target, { duration: 1.5 });
      });
    });
  }

  /* The claim banner rises word by word. It is the one thing above
     the buy area, and it animates for the same reason the buy area
     does not: it is a headline, not a price.

     Reduced motion is checked here rather than inside the scene —
     REDUCED is already read on this page, and a claim that is
     readable the instant it is drawn is exactly what that setting
     asks for. */
  if(!REDUCED) scenes.claimIn(gsap);

  // The buy area is deliberately NOT animated in. It is the first
  // thing on the page and the reason the page exists — fading it up
  // only delays the price.
  scenes.crossingLines(gsap, ScrollTrigger);
  scenes.marquee(gsap);
  scenes.counters(gsap, ScrollTrigger);
  scenes.footerCta(gsap);

  /* The three steps get the full beat — heading and captions rising
     word by word out of their masks, each clip opening from its own
     bottom edge — rather than the flat fade this used to be. The
     wording is exactly the wording in product-data.js; the scene only
     re-wraps it. See scenes.productSteps. */
  scenes.productSteps(gsap, ScrollTrigger);
  /* Why smartmop down to Shark Tank: headings rising word by word,
     the clips and the See the dirt photo opening on the scroll. See
     scenes.storyBeats. */
  if(!REDUCED) scenes.storyBeats(gsap, ScrollTrigger);

  /* The scroll beats belonging to any home page section added to
     this page — the poster rising, the stacking cards, the showreel
     canvas. Each one is gated on its own markup inside, so this
     costs a product page with no home sections four querySelector
     calls and nothing else. */
  initHomeScenes(gsap, ScrollTrigger);

  /* The gallery, the specs and the steps are all injected after the
     Storefront API answers, which is regularly AFTER `load` has
     already fired — so the single load listener this used to be
     could measure a page that was still an empty shell and never run
     again. watchLayout also catches each gallery image as it decodes.
     See st-refresh.js. */
  watchLayout(ScrollTrigger)();
}
